import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import { formatCurrency } from '../../utils';
import {
    CancelSaveMode,
    CancellationFeeChoice,
    CancellationFeeStep,
} from '../../api/booking/bookingWriteApiSlice';

interface Props {
    open: boolean;
    quote: CancellationFeeStep | null;
    loading: boolean;
    onClose: () => void;
    onConfirm: (saveMode: CancelSaveMode, fee?: CancellationFeeChoice) => void;
}

/** What the dialog says when no fee is to choose: it could not be computed, or the cancel is free. */
const NoFeeText = ({ quote }: { quote: CancellationFeeStep }) => {
    const { t } = useTranslation();
    if (quote.kind === 'undetermined') {
        return (
            <p className={'text-sm text-foreground'} role={'alert'}>
                {t(`booking_details.cancellation_fee.undetermined.${quote.reason}`)}
            </p>
        );
    }
    return <p className={'text-sm text-foreground'}>{t('booking_details.cancellation_fee.free')}</p>;
};

/**
 * The staff cancel of a booking whose cancellation rule charges a fee (M10,
 * W1-6, #3042). Opened instead of the plain cancel dialog when the booking's
 * rule has a tier in force, binds but its fee could not be computed, or the
 * cancel will correct an invoice already issued (D4 = A), which it then says.
 *
 * Staff charge the fee or waive it (PO, 2026-10-24, D1 = B). Neither is
 * preselected: the choice is the reason this dialog exists, so the commit
 * buttons stay disabled until one is made. As in the cancel and decline
 * dialogs, whether the customer is told is a button, never a checkbox.
 */
const CancellationFeeDialog = ({ open, quote, loading, onClose, onConfirm }: Props) => {
    const { t, i18n } = useTranslation();
    const [choice, setChoice] = useState<CancellationFeeChoice['choice'] | null>(null);
    const [pending, setPending] = useState<CancelSaveMode | null>(null);

    // A fresh choice for every booking: a waiver picked for one must not
    // carry over to the next.
    useEffect(() => {
        if (open) {
            setChoice(null);
            setPending(null);
        }
    }, [open]);

    if (!open || !quote) return null;

    const fee = quote.kind === 'fee' ? quote : null;
    const ready = !fee || choice !== null;
    const commit = (saveMode: CancelSaveMode) => {
        setPending(saveMode);
        onConfirm(saveMode, fee && choice ? { choice, expectedFee: fee.amount } : undefined);
    };
    const money = (value: number) => formatCurrency(value, i18n.language);

    return (
        <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
            <div className={'fixed inset-0 bg-black bg-opacity-50 transition-opacity'} onClick={onClose} />
            <div
                className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-auto'}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={'cancellation-fee-dialog-title'}
            >
                <div className={'p-6 flex flex-col gap-4'}>
                    <h3 className={'text-lg font-bold text-foreground'} id={'cancellation-fee-dialog-title'}>
                        {t('booking_details.cancellation_fee.header')}
                    </h3>
                    {fee ? (
                        <>
                            <p className={'text-sm text-foreground'}>
                                {t('booking_details.cancellation_fee.applies', {
                                    percent: fee.percent,
                                    base: money(fee.base),
                                    amount: money(fee.amount),
                                })}
                            </p>
                            <div className={'flex flex-col gap-2'} role={'radiogroup'}>
                                {(['charge', 'waive'] as const).map((value) => (
                                    <label className={'flex items-center gap-2'} key={value}>
                                        <input
                                            type={'radio'}
                                            name={'cancellation-fee-choice'}
                                            value={value}
                                            checked={choice === value}
                                            onChange={() => setChoice(value)}
                                            disabled={loading}
                                            className={'text-primary'}
                                        />
                                        <span className={'text-sm text-foreground'}>
                                            {value === 'charge'
                                                ? t('booking_details.cancellation_fee.charge', { amount: money(fee.amount) })
                                                : t('booking_details.cancellation_fee.waive')}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>
                    ) : (
                        <NoFeeText quote={quote} />
                    )}
                    {quote.liveInvoiceNumber && (
                        <p className={'text-sm text-foreground'}>
                            {t(fee
                                ? 'booking_details.cancellation_fee.invoice_notice_fee'
                                : 'booking_details.cancellation_fee.invoice_notice', { number: quote.liveInvoiceNumber })}
                        </p>
                    )}
                </div>
                <div className={'px-6 py-4 flex flex-row-reverse flex-wrap gap-3'}>
                    <CustomButton
                        title={t('booking_details.confirmDialog.rightButtonText')}
                        loading={loading && pending === 'save_and_send_email'}
                        disabled={loading || !ready}
                        onClick={() => commit('save_and_send_email')}
                    />
                    <CustomButton
                        title={t('booking_details.confirmDialog.middleButtonText')}
                        loading={loading && pending === 'save_only'}
                        disabled={loading || !ready}
                        onClick={() => commit('save_only')}
                        isFilled={false}
                    />
                    <div className={'mr-auto'}>
                        <CustomButton
                            title={t('booking_details.confirmDialog.leftButtonText')}
                            onClick={onClose}
                            isFilled={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CancellationFeeDialog;
